import { Bike, Heart, Inbox, Lock, Sparkles, Store } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

const CONTACT_MAILTO =
  'mailto:hey@bhookly.com?subject=' +
  encodeURIComponent('Enable Online Ordering for my restaurant')

const FEATURES = [
  {
    icon: Store,
    title: 'Your own branded storefront',
    text: 'Customers order from your website — your logo, colours, and menu. No marketplace commissions.',
  },
  {
    icon: Inbox,
    title: 'Live order inbox',
    text: 'New orders ring the counter with sound alerts. Accept or reject with one tap.',
  },
  {
    icon: Bike,
    title: 'Delivery & pickup, your rules',
    text: 'Configurable delivery fees, business hours, and order types.',
  },
  {
    icon: Heart,
    title: 'Customer accounts & loyalty',
    text: 'Repeat customers get saved addresses, order history, and loyalty rewards.',
  },
]

/**
 * Premium upsell shown wherever a tenant WITHOUT the online-ordering add-on
 * (FEATURE_ONLINE_ORDERING unset on their backend) lands on an
 * online-ordering surface. `full` fills the /admin/online-orders page;
 * `compact` is the smaller in-panel variant.
 */
export function OnlineOrderingPaywall({ variant = 'full' }: { variant?: 'full' | 'compact' }) {
  const full = variant === 'full'
  return (
    <div className={cn('flex items-center justify-center', full ? 'min-h-[70vh] p-6' : 'py-4')}>
      <Card className={cn('relative w-full overflow-hidden border-warning-border/60', full ? 'max-w-xl' : 'max-w-2xl')}>
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-300 via-amber-500 to-amber-300" aria-hidden />
        <CardContent className={cn('space-y-6 text-center', full ? 'p-8' : 'p-6')}>
          <span className="relative mx-auto inline-flex">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-amber-300 to-amber-500 shadow-lg shadow-amber-500/30">
              <Lock className="h-8 w-8 text-white" aria-hidden />
            </span>
            <Sparkles className="absolute -right-1 -top-1 h-5 w-5 text-amber-400" aria-hidden />
          </span>

          <div className="space-y-2">
            <Badge
              variant="outline"
              className="border-amber-400/50 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
            >
              Premium add-on
            </Badge>
            <h2 className="text-2xl font-bold tracking-tight">
              Online Ordering — a Bhookly Premium add-on
            </h2>
            <p className="text-muted-foreground">
              Take orders straight from your own branded website — this restaurant doesn&apos;t
              have Online Ordering enabled yet.
            </p>
          </div>

          <div className={cn('grid gap-3 text-left', full && 'sm:grid-cols-2')}>
            {FEATURES.map(({ icon: FeatureIcon, title, text }) => (
              <div key={title} className="flex gap-3 rounded-lg border bg-card p-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400">
                  <FeatureIcon className="h-5 w-5" aria-hidden />
                </span>
                <span>
                  <span className="block text-sm font-medium">{title}</span>
                  <span className="block text-xs text-muted-foreground">{text}</span>
                </span>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <Button
              asChild
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-md shadow-amber-500/25 hover:from-amber-600 hover:to-amber-700"
            >
              <a href={CONTACT_MAILTO}>Contact us to set it up</a>
            </Button>
            <p className="text-xs text-muted-foreground">
              Email <a className="underline underline-offset-2" href={CONTACT_MAILTO}>hey@bhookly.com</a> — we&apos;ll
              switch it on for your restaurant.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * Neutral placeholder rendered while `['settings','all']` resolves, so
 * neither the paywall nor the real dashboard flashes for the wrong tenant.
 */
export function OnlineOrderingGateSkeleton() {
  return (
    <div className="flex min-h-[70vh] items-center justify-center p-6">
      <div className="h-40 w-full max-w-xl animate-pulse rounded-xl bg-muted" />
    </div>
  )
}
